import type { FortuneCellData, FortuneSheet } from './types';

// Fortune-Sheet allocates a dense row×column matrix per sheet, so one stray
// value at row 1,048,562 costs ~27M cells. Cells past a gap this wide, on a
// sheet this tall, are parked outside the grid and handed back on save.
export const MAX_EMPTY_ROW_GAP = 1_000;
export const LARGE_SHEET_ROW_THRESHOLD = 10_000;
const MIN_TRIMMED_ROWS = 50;

export interface SparseTailSplit {
    sheet: FortuneSheet;
    tail: FortuneCellData[];
}

const findTailStartRow = (cells: FortuneCellData[]): number | null => {
    const rows = Array.from(new Set(cells.map((cell) => cell.r))).sort((a, b) => a - b);
    for (let i = 1; i < rows.length; i++) {
        if (rows[i] > LARGE_SHEET_ROW_THRESHOLD && rows[i] - rows[i - 1] > MAX_EMPTY_ROW_GAP) return rows[i];
    }
    return null;
};

/** Returns the same sheet reference and an empty tail when nothing is stranded
 *  (or a merge sits in the tail, which must stay inside the grid). */
export const splitSparseTail = (sheet: FortuneSheet): SparseTailSplit => {
    const cells = sheet.celldata ?? [];
    const tailStart = findTailStartRow(cells);
    if (tailStart === null) return { sheet, tail: [] };

    const kept = cells.filter((cell) => cell.r < tailStart);
    const tail = cells.filter((cell) => cell.r >= tailStart);
    if (tail.some((cell) => cell.v && typeof cell.v === 'object' && cell.v.mc)) return { sheet, tail: [] };

    const lastKeptRow = kept.reduce((max, cell) => Math.max(max, cell.r), 0);
    const row = Math.min(sheet.row ?? Infinity, Math.max(MIN_TRIMMED_ROWS, lastKeptRow + 1));
    return { sheet: { ...sheet, celldata: kept, row }, tail };
};
